import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { get, post, completeSignIn, isLoggedIn, getStoredUser, loadSession, setOrganizationId } from "../../utils/service";
import AuthShell, { GlassInputWrapper, authButton, authInput } from "@/components/auth/AuthShell";
import OAuthButtons from "@/components/auth/OAuthButtons";

interface InvitationInfo {
  email: string;
  first_name: string | null;
  last_name: string | null;
  organization: { name: string | null };
  role: { name: string | null };
  status: "pending" | "accepted" | "expired" | "revoked";
  has_account: boolean;
}

/**
 * The link in an invitation email. New people create their account with a password, or with
 * Google / Microsoft (the provider's verified email must match the invitation). People who already
 * have an account sign in and accept.
 */
export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", password: "", password_confirmation: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const signedIn = isLoggedIn();
  const me = getStoredUser();

  useEffect(() => {
    if (!token) { setNotFound(true); return; }
    get<{ data: InvitationInfo }>(`/auth/invitations/${encodeURIComponent(token)}`)
      .then((r) => {
        setInfo(r.data);
        setForm((f) => ({ ...f, first_name: r.data.first_name ?? "", last_name: r.data.last_name ?? "" }));
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const errorOf = (e: any, fallback: string) => {
    const errors = e?.response?.data?.errors as Record<string, string[] | string> | undefined;
    const first = errors && Object.entries(errors).find(([k]) => k !== "code")?.[1];
    return (Array.isArray(first) ? first[0] : first) || e?.response?.data?.message || fallback;
  };

  const createAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await post<{ data: { token: string; refresh_token: string } }>("/auth/invitations/accept", { token, ...form });
      await completeSignIn(r.data, true);
      toast.success(`Welcome to ${info?.organization.name ?? "your organization"}`);
      navigate("/");
    } catch (err) {
      setError(errorOf(err, "Could not create your account."));
    } finally {
      setBusy(false);
    }
  };

  const acceptAsMe = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await post<{ data: { organization_id: string } }>(`/auth/invitations/${encodeURIComponent(token)}/accept`, {});
      setOrganizationId(r.data.organization_id);
      await loadSession();
      toast.success(`You joined ${info?.organization.name ?? "the organization"}`);
      navigate("/");
    } catch (err) {
      setError(errorOf(err, "Could not accept the invitation."));
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <AuthShell title="Invitation not found" subtitle="This link is incomplete or no longer exists. Ask your administrator to send a new invitation.">
        <Link to="/login" className={`${authButton} block text-center`}>Go to sign in</Link>
      </AuthShell>
    );
  }

  if (!info) {
    return (
      <AuthShell title="Opening your invitation" subtitle="One moment…">
        <div className="h-1 w-full overflow-hidden rounded-full bg-gray-100"><div className="h-full w-1/3 animate-pulse rounded-full bg-[#ff6d27]" /></div>
      </AuthShell>
    );
  }

  const orgName = info.organization.name ?? "an organization";

  if (info.status !== "pending") {
    const why = { accepted: "This invitation has already been accepted.", expired: "This invitation has expired.", revoked: "This invitation was withdrawn." }[info.status];
    return (
      <AuthShell title="This invitation can't be used" subtitle={`${why} Ask an administrator of ${orgName} to send you a new one.`}>
        <Link to="/login" className={`${authButton} block text-center`}>Go to sign in</Link>
      </AuthShell>
    );
  }

  // Someone with an account: sign in (as the invited email), then accept.
  if (info.has_account) {
    const sameUser = signedIn && me?.email?.toLowerCase() === info.email.toLowerCase();
    return (
      <AuthShell title={`Join ${orgName}`} subtitle={`You've been invited as ${info.role.name ?? "a member"}. You already have an account for ${info.email}.`}>
        {sameUser ? (
          <button onClick={acceptAsMe} disabled={busy} className={authButton}>{busy ? "Joining..." : `Accept and open ${orgName}`}</button>
        ) : (
          <>
            {signedIn && <p className="text-xs text-amber-700">You're signed in as {me?.email}. Sign out and sign in as {info.email} to accept.</p>}
            <Link to={`/login?next=${encodeURIComponent(`/accept-invitation?token=${token}`)}`} className={`${authButton} block text-center`}>Sign in to accept</Link>
          </>
        )}
        {error && <p className="text-red-500 text-xs">{error}</p>}
      </AuthShell>
    );
  }

  return (
    <AuthShell title={`Join ${orgName}`} subtitle={<>You've been invited as <strong>{info.role.name ?? "a member"}</strong>. Create your account for <strong>{info.email}</strong>.</>}>
      <form className="space-y-4" onSubmit={createAccount}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-700">First name</label>
            <GlassInputWrapper><input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={authInput} /></GlassInputWrapper>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Last name</label>
            <GlassInputWrapper><input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={authInput} /></GlassInputWrapper>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700">Password</label>
          <GlassInputWrapper>
            <div className="relative">
              <input type={show ? "text" : "password"} required autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={`${authInput} pr-12`} placeholder="At least 10 characters, upper & lower case and a number" />
              <button type="button" onClick={() => setShow(!show)} className="absolute inset-y-0 right-3 flex items-center">{show ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}</button>
            </div>
          </GlassInputWrapper>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700">Confirm password</label>
          <GlassInputWrapper><input type={show ? "text" : "password"} required autoComplete="new-password" value={form.password_confirmation} onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })} className={authInput} /></GlassInputWrapper>
        </div>
        {error && <p className="text-red-500 text-xs">{error}</p>}
        <button type="submit" disabled={busy} className={authButton}>{busy ? "Creating your account..." : "Create account and join"}</button>
      </form>
      <OAuthButtons query={{ intent: "invite", invitation: token }} label="Join with" />
      <p className="text-[11px] text-gray-500">To join with Google or Microsoft, that account's email must be {info.email}.</p>
    </AuthShell>
  );
}
